import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Listbox, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import type { JSX } from 'react';

interface Reason {
  id: string;
  name: string;
}

interface ReasonDropdownProps {
  reasons: Reason[];
  onReasonChange: (reason: Reason) => void;
}

function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(' ');
}

export default function ReasonDropdown({
  reasons,
  onReasonChange
}: ReasonDropdownProps): JSX.Element {
  const [selected, setSelected] = useState<Reason | undefined>(undefined);

  const handleChange = (reason: Reason) => {
    setSelected(reason);
    onReasonChange(reason);
  };

  return (
    <Listbox value={selected} onChange={handleChange}>
      {({ open }) => (
        <>
          <Listbox.Label className="sr-only">Select a reason</Listbox.Label>
          <div className="relative">
            <div
              className={`inline-flex divide-x divide-gray-300 rounded-md shadow-sm ${!selected}`}>
              <div className="inline-flex items-center gap-x-1.5 rounded-l-md bg-gray-200 px-3 py-2 text-gray-800">
                <p className="text-sm font-semibold">
                  {selected ? selected.name : 'Select a reason'}
                </p>
              </div>
              <Listbox.Button
                data-testid="reason-dropdown"
                className="inline-flex items-center rounded-l-none rounded-r-md bg-gray-200 p-2 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-gray-500">
                <span className="sr-only">Select a reason</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="h-5 w-5 text-gray-800"
                  aria-hidden="true"
                />
              </Listbox.Button>
            </div>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <Listbox.Options className="absolute z-10 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {reasons.map((reason) => (
                  <Listbox.Option
                    data-testid={`reason-dropdown-option-${reason.id}`}
                    key={reason.id}
                    className={({ active }) =>
                      classNames(
                        active ? 'bg-gray-200 text-gray-900' : 'text-gray-900',
                        'cursor-default select-none p-4 text-sm'
                      )
                    }
                    value={reason}>
                    {({ selected, active }) => (
                      <div className="flex justify-between">
                        <p className={selected ? 'font-semibold' : 'font-normal'}>{reason.name}</p>
                        {selected ? (
                          <span className={active ? 'text-gray-900' : 'text-gray-600'}>
                            <FontAwesomeIcon
                              icon={faCheck}
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}
